import { useEffect } from "react";

import {
  FacebookIcon,
  LinkedinIcon,
  TwitterIcon,
  WhatsappIcon,
} from "../../SocialIcons";
import CopyLink from "../../CopyLink";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { link, pageview } from "@qoohoo/common/constants/analytics";
import { sendShareEventViaPixel } from "@qoohoo/common/utils/analytics/pixel";
import { addTagsInRecording } from "@qoohoo/common/utils/analytics/clarity";
import useSessionStore from "../../../store/sessionStore";
import {
  getWhatsappShareUrl,
  prepareShareObject,
} from "@qoohoo/common/utils/social-share";

const ShareModal = ({
  message,
  url,
  shareText,
  creatorDetails,
  closeModal,
  isWhiteTheme
}) => {
  const userData = useSessionStore((state) => state.userData);
  const currentUsername = userData.username;

  const shortEncodedUrl = encodeURIComponent(url);
  const nonUrlShareableEncodedUrl = encodeURIComponent(message);

  const shareObject = prepareShareObject(url, message);

  const current_screen = pageview.channel_details;
  const modal_name = "share_modal";
  const trackDetails = {
    current_user: currentUsername,
    current_screen,
    modal_name,
    short_url: url,
    message,
    is_telegram: true,
  };

  function getWhatsappShareText() {
    const whatsappShareText = getWhatsappShareUrl({
      text: shareObject.encodedTextWithUrl,
    });

    return whatsappShareText;
  }

  useEffect(() => {
    addTagsInRecording({ ...trackDetails, share_modal: true, shareUrl: url });
    sendShareEventViaPixel({ ...creatorDetails, currentUsername });
    Track.customEvent(modal_name, {
      actionType: "modal_open",
      label: modal_name,
      ...trackDetails,
    });
  }, []);

  return (
    <div className="inline-flex items-end justify-center w-full h-full sm:items-center font-nunito">
      <div className="relative bg-[#22272B] shadow-xl rounded-t-2xl sm:rounded-2xl max-w-full w-full sm:max-w-[360px] sm:w-[360px] dark:bg-white">
        <div className="absolute -left-2 right-0 mx-auto -top-28 w-[347px] sm:w-[373px]">
          <img
            src="/assets/images/static-confetti.svg"
            alt="Share modal confetti"
          />
        </div>
        <div className="absolute -top-12 right-0 left-0 mx-auto flex items-center justify-center w-24 h-24 rounded-full bg-[#22272B] dark:bg-white">
          <img
            className="w-[88px] h-[88px] object-cover object-center rounded-full select-none"
            src={creatorDetails.avatar.url}
            alt={creatorDetails.fullname || creatorDetails.username}
          />
        </div>
        {
          isWhiteTheme?
          <img
          src="/assets/icons/close-icon-dark.svg"
          lt="close button"
          onClick={closeModal}
          className="absolute z-10 cursor-pointer select-none right-4 top-4"
          />
          :
          <img
          src="/assets/icons/close-icon.svg"
          lt="close button"
          onClick={closeModal}
          className="absolute z-10 cursor-pointer select-none right-4 top-4"
          />
        }
        <div className="w-full px-6 pb-10 pt-[70px]">
          <div className="flex flex-col items-center justify-center w-full">
            <div className="text-2xl font-bold dark:text-[#16191B]">Share with friends</div>
            <div className="mt-4 text-[#F0F0F0] dark:text-[#16191B]">
              <p className="italic">“Your share is like a horse power”</p>
              <span className="italic">- A quote by cowboy smiley </span>
              <span>🤠</span>
            </div>
            <div className="flex flex-row items-center mt-16 space-x-6">
              <a
                href={getWhatsappShareText()}
                target="_blank"
                className="fill-[#E8E8E8] dark:fill-[#16191B] hover:fill-[#25D366] dark:hover:fill-[#25D366] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "whatsapp",
                  });

                  Track.linkClick(link.share_via_whatsapp, {
                    ...trackDetails,
                    url: getWhatsappShareText(),
                  });
                }}
              >
                <WhatsappIcon />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shortEncodedUrl}&quote=${nonUrlShareableEncodedUrl}`}
                target="_blank"
                className="fill-[#E8E8E8] dark:fill-[#16191B] hover:fill-[#1877F2] dark:hover:fill-[#1877F2] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "facebook",
                  });

                  Track.linkClick(link.share_via_facebook, {
                    ...trackDetails,
                    url: `https://www.facebook.com/sharer/sharer.php?u=${shortEncodedUrl}&quote=${nonUrlShareableEncodedUrl}`,
                  });
                }}
              >
                <FacebookIcon />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${nonUrlShareableEncodedUrl}&url=${shortEncodedUrl}`}
                target="_blank"
                className="fill-[#E8E8E8] dark:fill-[#16191B] hover:fill-[#1DA1F2] dark:hover:fill-[#1DA1F2] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "twitter",
                  });

                  Track.linkClick(link.share_via_twitter, {
                    ...trackDetails,
                    url: `https://twitter.com/intent/tweet?text=${nonUrlShareableEncodedUrl}&url=${shortEncodedUrl}`,
                  });
                }}
              >
                <TwitterIcon />
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${shortEncodedUrl}&title=${nonUrlShareableEncodedUrl}`}
                target="_blank"
                className="fill-[#E8E8E8] dark:fill-[#16191B] hover:fill-[#00A0DC] dark:hover:fill-[#00A0DC] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "linkedin",
                  });

                  Track.linkClick(link.share_via_linkedin, {
                    ...trackDetails,
                    url: `https://www.linkedin.com/shareArticle?mini=true&url=${shortEncodedUrl}&title=${nonUrlShareableEncodedUrl}`,
                  });
                }}
              >
                <LinkedinIcon />
              </a>
            </div>

            <div className="text-sm my-6 font-normal text-[#929292] dark:text-[#393939]">or</div>
            <CopyLink
              copyText={shareText}
              url={url}
              message={message}
              modalName={modal_name}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
